import {cloneDeep, filter, size} from 'lodash';
import fp, {flow} from 'lodash/fp';
import React, {useMemo} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';

import {openModal} from 'shared/actions/modal';
import {Store, ChecklistItem} from 'src/types/store';

import {CITIZENSHIP_MODAL} from 'src/components/citizenship-modal';
import {CURRENT_SCHOOL_YEAR} from 'shared/utils';
import Checklist from '../checklist';

interface Props {
  onDismiss: () => void;
  navigateToCollegeSearch: () => void;
}

const HSChecklist = ({onDismiss, navigateToCollegeSearch}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const student = useSelector((state: Store) => state.student);
  const bulkEntryCourses = useSelector((state: Store) => state.bulkEntryCourses);
  const activities = useSelector((state: Store) => state.activities);
  const communityServiceEvents = useSelector((state: Store) => state.communityServiceEvents);
  const testScores = useSelector((state: Store) => state.testScores);
  const graduationYear = student.highSchoolInfo.graduationYear;
  const totalCredits = flow(fp.map('credits'), fp.sum)(bulkEntryCourses);

  const checklistItems: Record<string, ChecklistItem> = useMemo(() => {
    // Initialize checklist items.
    const BASE_ITEMS = {
      'select-high-school': {
        title: 'Select your high school',
        description: '',
        allowSkip: false,
        url: '/account-settings',
        ctaLabel: 'Select your high school',
        isComplete: () => size(student.currentInstitution) > 0,
        order: 0,
      },
      citizenship: {
        title: 'Add your citizenship status',
        description: 'Colleges have scholarships for US and international students.',
        allowSkip: true,
        cta: () => dispatch(openModal(CITIZENSHIP_MODAL)),
        ctaLabel: 'Add Citizenship',
        isComplete: () => typeof student.isUsCitizenOrResident === 'boolean',
        order: 20,
      },
      'add-courses': {
        title: 'Add 6 courses total to your portfolio',
        description: 'Add 6 courses to see your earnings.',
        allowSkip: false,
        cta: () => history.replace(location.pathname + '/course/new', {skipScrolling: true}),
        ctaLabel: 'Add Courses',
        isComplete: () => Object.keys(bulkEntryCourses).length >= 6,
        order: 30,
        graduationYear,
        type: 'six courses',
      },
      'follow-a-college': {
        title: 'Follow at least one college',
        description: 'See your potential earnings for followed colleges.',
        allowSkip: false,
        cta: () => navigateToCollegeSearch(),
        ctaLabel: 'Discover Colleges',
        isComplete: () => !!student.followedCollegeIds.length,
        order: 40,
      },
      'add-extracurricular': {
        title: 'Add 1 Extracurricular Activity to your portfolio',
        description: 'Add to your portfolio and earn even more.',
        allowSkip: false,
        cta: () => history.replace(location.pathname + '/activity/new', {skipScrolling: true}),
        ctaLabel: 'Add Extracurricular',
        isComplete: () => size(activities) > 0,
        order: 50,
        graduationYear,
        type: 'extracurricular',
      },
      'add-community-service': {
        title: 'Add 1 Community Service to your portfolio',
        description: 'Add to your portfolio and see how much you could earn.',
        allowSkip: false,
        cta: () =>
          history.replace(location.pathname + '/community-service/new', {skipScrolling: true}),
        ctaLabel: 'Add Community Service',
        isComplete: () =>
          Object.values(communityServiceEvents).filter((event) => !!event._id).length > 0,
        order: 60,
        graduationYear,
        type: 'community service',
      },
      referral: {
        title: 'Invite a Friend to Raise Me',
        description: 'Some colleges award a micro-scholarship for this.',
        allowSkip: true,
        ctaLabel: 'Invite',
        cta: () =>
          history.replace(location.pathname + '/refer', {skipScrolling: true, showSkip: true}),
        isComplete: () => !!student.studentInvitations?.length,
        order: 80,
        graduationYear,
        type: 'referral',
      },
    };

    const JUNIOR_SENIOR_ITEMS = {
      'add-ten-credits': {
        title: 'Add 10 credits to see how balanced your college list is.',
        description: 'See which colleges are a safety, target, or reach.',
        allowSkip: false,
        cta: () => history.replace(location.pathname + '/course/new', {skipScrolling: true}),
        ctaLabel: 'Add Courses',
        isComplete: () => totalCredits >= 10,
        order: 45,
      },
      'add-test-score': {
        title: 'Add score for PSAT/PreACT',
        description: 'Add your scores and you may qualify for additional micro-scholarships.',
        allowSkip: true,
        cta: () =>
          history.replace(location.pathname + '/test-score/new?tests=psat2015,preAct', {
            skipScrolling: true,
          }),
        ctaLabel: 'Add Test Score',
        isComplete: () =>
          size(filter(testScores, {type: 'psat2015'})) +
            size(filter(testScores, {type: 'preAct'})) >
          0,
        order: 70,
        graduationYear,
        type: 'test score',
      },
    };

    const items = cloneDeep(BASE_ITEMS);
    if (graduationYear === CURRENT_SCHOOL_YEAR + 1 || graduationYear === CURRENT_SCHOOL_YEAR) {
      Object.assign(items, JUNIOR_SENIOR_ITEMS);
    }
    return items;
  }, [
    activities,
    bulkEntryCourses,
    communityServiceEvents,
    dispatch,
    graduationYear,
    history,
    location.pathname,
    navigateToCollegeSearch,
    student.currentInstitution,
    student.followedCollegeIds.length,
    student.isUsCitizenOrResident,
    student.studentInvitations,
    testScores,
    totalCredits,
  ]);

  return (
    <Checklist
      firstName={student.firstName}
      skippedChecklistItems={student.skippedChecklistItems}
      listItems={checklistItems}
      onDismiss={onDismiss}
      tipContent="Keep adding all your courses (no matter what grade you earned) to help match you to the best colleges."
      onCourseAdd={() => {
        history.replace(location.pathname + '/course/new', {skipScrolling: true});
      }}
    />
  );
};

export default HSChecklist;
